// Reads the list table the user is looking at — a <DataTable> (`[data-pdf-table]`) on desktop, or its
// <MobileRows> cards on narrow screens — into plain data the PDF builder lays out:
//
//   { columns: [{ label, width }], rows: [[{ content, rowSpan?, colSpan? }]] }
//
// Only what is on screen: the current page of rows, the shown columns, the current tab. Controls are
// left out (buttons, inputs, icons, anything marked `data-pdf-ignore` / `data-html2canvas-ignore`), and
// so are whole columns with no label — the selection checkbox and row-action columns, which DataTable
// marks `data-pdf-skip`. A dropdown that shows a value (a status select) keeps that value: it is data.

const IGNORED_SELECTOR = [
  'button',
  'input',
  'textarea',
  'svg',
  'img',
  '[role="button"]',
  '[data-pdf-ignore]',
  '[data-html2canvas-ignore]',
  // react-select: its placeholder ("Select…") is not a value.
  '[class*="__placeholder"]',
].join(',');

// The value a react-select shows (single value, or each chip of a multi value), and its control box.
const SELECT_VALUE_SELECTOR = '[class*="__single-value"], [class*="__multi-value__label"]';
const SELECT_ROOT_SELECTOR = '[class*="__control"]';

const BLOCK_DISPLAYS = ['block', 'flex', 'grid', 'list-item', 'table', 'table-row', '-webkit-box'];

const isHidden = (el) => {
  if (el.hidden) return true;
  const style = window.getComputedStyle(el);
  return style.display === 'none' || style.visibility === 'hidden';
};

/** Text of an element as it reads on screen: block children on their own lines, controls left out,
 *  the user's own line breaks kept where the element preserves them (note text is `pre-wrap`). */
export const readCellText = (root) => {
  let out = '';
  const newline = () => {
    if (out && !out.endsWith('\n')) out += '\n';
  };
  const walk = (node, preserve) => {
    if (node.nodeType === Node.TEXT_NODE) {
      out += preserve ? node.textContent : node.textContent.replace(/\s+/g, ' ');
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE || isHidden(node)) return;
    if (node.tagName === 'BR') {
      out += '\n';
      return;
    }
    // A react-select keeps the value it shows; everything else inside it (input, arrow) is chrome.
    if (node.matches(SELECT_ROOT_SELECTOR) && node.querySelector(SELECT_VALUE_SELECTOR)) {
      out += Array.from(node.querySelectorAll(SELECT_VALUE_SELECTOR))
        .map((value) => value.textContent.trim())
        .filter(Boolean)
        .join(', ');
      return;
    }
    if (node.matches(IGNORED_SELECTOR)) return;
    const style = window.getComputedStyle(node);
    const block = BLOCK_DISPLAYS.includes(style.display);
    const keepBreaks = preserve || /^pre/.test(style.whiteSpace || '');
    if (block) newline();
    node.childNodes.forEach((child) => walk(child, keepBreaks));
    if (block) newline();
  };
  walk(root, false);
  return out
    .split('\n')
    .map((line) => line.replace(/[ \t\u00a0]+/g, ' ').trim())
    .filter(Boolean)
    .join('\n');
};

const readDesktopTable = (table) => {
  const headerCells = Array.from(table.querySelectorAll('thead tr:first-child > th'));
  const keep = headerCells.map((th) => !th.hasAttribute('data-pdf-skip'));
  const columns = headerCells
    .filter((_, i) => keep[i])
    .map((th) => ({
      label: th.getAttribute('data-pdf-label') || readCellText(th),
      width: th.getBoundingClientRect().width,
    }));

  const rows = Array.from(table.querySelectorAll('tbody > tr'))
    .filter((tr) => !isHidden(tr))
    .map((tr) =>
      Array.from(tr.children)
        .filter((td) => !td.hasAttribute('data-pdf-skip'))
        .map((td) => {
          const cell = { content: readCellText(td) };
          if (td.rowSpan > 1) cell.rowSpan = td.rowSpan;
          if (td.colSpan > 1) cell.colSpan = td.colSpan;
          return cell;
        }),
    )
    .filter((cells) => cells.length > 0);

  return { columns, rows };
};

// Cards: every labelled field becomes a column, in the order the cards show them.
const readMobileCards = (list) => {
  const cards = Array.from(list.querySelectorAll('[data-pdf-card]')).filter((card) => !isHidden(card));
  const labels = [];
  const records = cards.map((card) => {
    const record = {};
    card.querySelectorAll('[data-pdf-field]').forEach((field) => {
      const label = readCellText(field.querySelector('[data-pdf-label]') || field);
      const valueEl = field.querySelector('[data-pdf-value]');
      if (!label || !valueEl) return;
      if (!labels.includes(label)) labels.push(label);
      record[label] = readCellText(valueEl);
    });
    return record;
  });
  return {
    columns: labels.map((label) => ({ label, width: 1 })),
    rows: records.map((record) => labels.map((label) => ({ content: record[label] || '' }))),
  };
};

/** The visible list table on the page, or null when none is shown (a map view, a media gallery). */
export const findVisibleTable = (root = document) =>
  Array.from(root.querySelectorAll('[data-pdf-table]')).find((el) => !isHidden(el) && el.isConnected) || null;

const readVisibleTable = (root = document) => {
  const container = findVisibleTable(root);
  if (!container) return null;
  // Not a table inside `data-pdf-ignore`: a scrollable table's pinned header copy (DataTable's
  // StickyHead) is a header-only <table> drawn before the real one.
  const table = Array.from(container.querySelectorAll('table')).find((t) => !t.closest('[data-pdf-ignore]'));
  if (table && !isHidden(table)) return readDesktopTable(table);
  const cards = container.querySelector('[data-pdf-cards]');
  if (cards && !isHidden(cards)) return readMobileCards(cards);
  return null;
};

export default readVisibleTable;
